import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

// Owner-only: move the parent Jubelio mapping onto a variant of the same product.
export default function MoveMappingModal({ product, onClose, onDone }) {
    const code = product.sku || "";
    const candidates = (product.variants || []).filter(
        (v) => (v.sku || "").trim().toLowerCase() === code.toLowerCase() && v.jubelio_item_id == null
    );
    const [variantId, setVariantId] = useState(candidates[0]?.id || "");
    const [label, setLabel] = useState("");
    const [parentSku, setParentSku] = useState("");
    const [busy, setBusy] = useState(false);
    const target = variantId ? candidates.find((v) => v.id === variantId)?.label : label || "New variant";
    const inp = "w-full border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark";

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/products/${product.id}/move-jubelio-mapping-to-variant`, {
                variant_id: variantId || null,
                new_variant_label: variantId ? null : label,
                new_parent_sku: parentSku,
            });
            toast.success("Jubelio mapping moved to variant");
            onDone(data);
        } catch (ex) {
            toast.error(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/40 overflow-y-auto" data-testid="move-mapping-modal">
            <div className="min-h-full flex items-center justify-center p-4">
                <div className="bg-white w-full max-w-md rounded-sm p-5 md:p-6 space-y-4 font-sans text-sm">
                    <h2 className="font-serif text-2xl">Move Jubelio mapping?</h2>
                    <div className="space-y-2">
                        <div className="eyebrow">Destination variant</div>
                        {candidates.map((v) => (
                            <label key={v.id} className="flex items-center gap-2">
                                <input type="radio" checked={variantId === v.id} onChange={() => setVariantId(v.id)} data-testid={`move-mapping-variant-${v.id}`} />
                                {v.label} · <span className="font-mono">{v.sku}</span>
                            </label>
                        ))}
                        <label className="flex items-center gap-2">
                            <input type="radio" checked={!variantId} onChange={() => setVariantId("")} data-testid="move-mapping-new-variant" />
                            Create new variant with SKU <span className="font-mono">{code}</span>
                        </label>
                        {!variantId && (
                            <input className={inp} placeholder="Variant label, e.g. Size 2" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="move-mapping-label" />
                        )}
                    </div>
                    <div className="space-y-1">
                        <div className="eyebrow">New parent SKU (internal, not sent to Jubelio)</div>
                        <input className={`${inp} font-mono`} placeholder="e.g. legjeans" value={parentSku} onChange={(e) => setParentSku(e.target.value)} data-testid="move-mapping-parent-sku" />
                    </div>
                    <div className="bg-cream/60 border border-mist p-3 space-y-1 text-[13px]" data-testid="move-mapping-summary">
                        <div><span className="text-neutral-500">Current:</span> Product → <span className="font-mono">{code}</span> → Jubelio item {product.jubelio_product_id}</div>
                        <div><span className="text-neutral-500">New:</span> {target} variant → <span className="font-mono">{code}</span> → Jubelio item {product.jubelio_product_id}</div>
                        <div className="text-neutral-600 pt-1">This will move the mapping inside the same product. No Jubelio data will be changed.</div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} disabled={busy} className="border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest" data-testid="move-mapping-cancel">Cancel</button>
                        <button onClick={submit} disabled={busy || !parentSku.trim() || (!variantId && !label.trim())}
                            className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest disabled:opacity-40" data-testid="move-mapping-confirm">
                            {busy ? "Moving…" : "Move mapping"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
